/**
 * Auto-generates tooltip descriptions for clip producer theme presets.
 *
 * Uses the backend-provided `userPromptDescription` when available,
 * otherwise derives a concise description from the theme label.
 * All tooltips are capped at 95 characters for consistent UI sizing.
 *
 * To add new themes, simply add an entry to THEME_TOOLTIPS.
 * Unknown themes get a generic fallback derived from the label.
 */

const MAX_TOOLTIP_LENGTH = 95;

/**
 * Map of known theme labels (lowercased) to their tooltip descriptions.
 * Each entry describes the *outcome* — what kind of clips the user will get.
 */
const THEME_TOOLTIPS: Record<string, string> = {
  'thought leadership':
    'Highlights moments of expert insight, bold opinions, and forward-thinking ideas.',
  'salient quote':
    'Surfaces the most memorable and shareable quotes from the conversation.',
  inspirational:
    'Finds uplifting moments that motivate and resonate emotionally with viewers.',
  cutdown:
    'Creates a concise summary capturing the key highlights of the full video.',
  'thought leadership (index-based)':
    'Uses transcript indexing to find precise moments of expert commentary.',
  emotional:
    'Captures moments of genuine emotion, vulnerability, or heartfelt expression.',
  surprising:
    'Finds unexpected revelations, plot twists, or counterintuitive insights.',
  educational:
    'Selects segments that teach, explain, or break down complex topics clearly.',
  humorous: 'Surfaces lighthearted and funny moments that entertain viewers.',
  controversial:
    'Highlights bold or debatable statements that spark conversation.',
  storytelling:
    'Finds compelling narrative arcs and personal anecdotes from the speaker.',
  'call to action':
    'Captures moments where the speaker drives urgency or encourages next steps.',
  motivational:
    'Selects empowering segments that inspire action and positive change.',
  'customer success':
    'Highlights real examples of customer wins and positive outcomes.',
  'product highlight':
    'Finds moments where key product features or benefits are showcased.',
  'pain point':
    'Surfaces discussions about challenges and problems the audience relates to.',
  'how-to':
    'Selects step-by-step explanations and practical guidance from the speaker.',
  announcement:
    'Captures key reveals, launches, or important updates shared in the video.',
  testimonial:
    'Finds authentic endorsements and positive experiences shared by speakers.',
  debate: 'Highlights back-and-forth exchanges and contrasting viewpoints.',
};

/**
 * Generates a generic fallback tooltip from a theme label.
 * Produces a description like "Finds clips focused on [theme] moments."
 */
function generateFallbackTooltip(themeLabel: string): string {
  const label = themeLabel.toLowerCase().replace(/[()]/g, '').trim();
  return truncate(`Finds clips focused on ${label} moments from the video.`);
}

function truncate(text: string): string {
  if (text.length <= MAX_TOOLTIP_LENGTH) return text;
  return text.slice(0, MAX_TOOLTIP_LENGTH - 1).trimEnd() + '\u2026';
}

/**
 * Returns a tooltip description for a clip producer theme.
 *
 * Priority:
 * 1. Curated tooltip from THEME_TOOLTIPS map (for known themes)
 * 2. Backend-provided `userPromptDescription` (if non-empty)
 * 3. Auto-generated fallback from label
 *
 * @param themeLabel - The display title of the theme preset
 * @param userPromptDescription - Optional backend-provided description
 * @returns A tooltip string, max 95 characters
 */
export function getThemeTooltip(
  themeLabel: string,
  userPromptDescription?: string | null,
): string {
  // Always prefer curated tooltips for known themes
  const key = themeLabel.toLowerCase().trim();
  const known = THEME_TOOLTIPS[key];
  if (known) return truncate(known);

  // For unknown themes, use backend description if available
  if (userPromptDescription && userPromptDescription.trim().length > 0) {
    return truncate(userPromptDescription.trim());
  }

  // Auto-generate from label
  return generateFallbackTooltip(themeLabel);
}
